import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 入力が上限を超えていないか（前後の空白は数えない。サーバの検証と同じく文字列の長さで数える）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の入力が字数の上限に収まっているかを、サーバの検証と同じ数え方で確かめる
 * @graph-connects none
 */
export function withinLimit(value: string, max: number): boolean {
  return value.trim().length <= max;
}

/**
 * 字数の上限つきの入力欄。上限を超えたら残り字数（マイナス）を赤で出す（タイトル80字・文2,000字）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の文の入力欄に、字数と上限を出し、超えたら超えた字数を赤で出す
 * @graph-connects withinLimit [calls] 上限の確かめ
 */
export function CountedTextField({
  label,
  value,
  onChange,
  max,
  multiline = false,
  rows = 4,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  max: number;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const length = value.trim().length;
  const over = !withinLimit(value, max);
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]"
      >
        {label}
      </label>
      {multiline ? (
        <Textarea
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ) : (
        <Input
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      )}
      <p
        className={`mt-1 text-right text-xs ${
          over ? 'font-semibold text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'
        }`}
      >
        {over
          ? t('issueMgmt.form.overLimit', { n: String(length - max) })
          : `${String(length)} / ${String(max)}`}
      </p>
    </div>
  );
}
